import { secure } from '#library/proxy.library.js';
/**
 * Creates a normalized cache facade exposing safe operations over the active state's BoundedCache
 * and WeakCache.
 */
export function createCacheFacade(getState) {
    const weak = secure({
        /** Retrieves a weakly held object from the cache by key */
        get(key) {
            const normalized = String(key).trim().toLowerCase();
            return getState().weakCache.get(normalized);
        },
        /** Checks if a key exists in the weak cache and its value is not garbage collected */
        has(key) {
            const normalized = String(key).trim().toLowerCase();
            return getState().weakCache.has(normalized);
        },
        /** Sets a weakly held object value in the cache */
        set(key, value) {
            const normalized = String(key).trim().toLowerCase();
            getState().weakCache.set(normalized, value);
            return this;
        },
        /** Returns the cached value or computes and caches it using the factory function */
        getOrSet(key, factory) {
            const normalized = String(key).trim().toLowerCase();
            return getState().weakCache.getOrSet(normalized, factory);
        },
        /** Deletes a key from the weak cache */
        delete(key) {
            const normalized = String(key).trim().toLowerCase();
            return getState().weakCache.delete(normalized);
        },
        /** Clears all entries from the weak cache */
        clear() {
            getState().weakCache.clear();
        },
        /** Returns true to identify this instance as a WeakCache facade */
        get isWeakCache() {
            return true;
        }
    });
    return secure({
        /** Retrieves a value from the cache by key */
        get(key) {
            const normalized = String(key).trim().toLowerCase();
            return getState().cache.get(normalized);
        },
        /** Checks if a key exists in the cache */
        has(key) {
            const normalized = String(key).trim().toLowerCase();
            return getState().cache.has(normalized);
        },
        /** Sets a value in the cache */
        set(key, value) {
            const normalized = String(key).trim().toLowerCase();
            getState().cache.set(normalized, String(value));
            return this;
        },
        /** Sets a static (immortal) value in the cache that won't expire */
        setStatic(key, value) {
            const normalized = String(key).trim().toLowerCase();
            getState().cache.setStatic(normalized, String(value));
            return this;
        },
        /** Deletes a key from the cache */
        delete(key) {
            const normalized = String(key).trim().toLowerCase();
            return getState().cache.delete(normalized);
        },
        /** Deletes all keys starting with the given prefix */
        deletePrefix(prefix) {
            const normalizedPrefix = String(prefix).trim().toLowerCase();
            return getState().cache.deletePrefix(normalizedPrefix);
        },
        /** Clears the cache, optionally evicting only a specific count of entries */
        clear(count) {
            getState().cache.clear(count);
        },
        /** Returns an iterator of cache entries */
        entries() {
            return getState().cache.entries();
        },
        /** Populates the cache from an iterable of key-value pairs */
        fromEntries(entries) {
            for (const [k, v] of entries) {
                const normalized = String(k).trim().toLowerCase();
                getState().cache.set(normalized, String(v));
            }
            return this;
        },
        /** Converts the cache to a JSON object */
        toJSON() {
            return getState().cache.toJSON();
        },
        /** Weak-value object cache facade */
        weak,
    });
}
